import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { logout } from '../store/slices/authSlice';
import { logout as logoutService } from '../services/authService';
import { getTokenExpiration, isTokenExpired } from '../utils/jwt';

const CHECK_INTERVAL_MS = 60 * 1000;
const EXPIRATION_BUFFER_SECONDS = 30;

export function useTokenExpiration(): void {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Store dispatch and navigate in refs so timer callbacks always use the
  // latest version without needing to be included in useEffect dependencies.
  const dispatchRef = useRef(dispatch);
  const navigateRef = useRef(navigate);
  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const performLogout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    logoutService();
    dispatchRef.current(logout());
    sessionStorage.setItem(
      'auth_redirect_message',
      'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
    );
    navigateRef.current('/login', { replace: true });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (!token) return;

    if (isTokenExpired(token, EXPIRATION_BUFFER_SECONDS)) {
      performLogout();
      return;
    }

    const exp = getTokenExpiration(token);
    if (exp === null) return;

    const expiresAtMs = (exp - EXPIRATION_BUFFER_SECONDS) * 1000;
    const delayMs = expiresAtMs - Date.now();

    if (delayMs <= 0) {
      performLogout();
      return;
    }

    timeoutRef.current = setTimeout(performLogout, delayMs);

    intervalRef.current = setInterval(() => {
      const currentToken = localStorage.getItem('auth_token');
      if (!currentToken || isTokenExpired(currentToken, EXPIRATION_BUFFER_SECONDS)) {
        performLogout();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAuthenticated, performLogout]);
}
